import React, { useEffect, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Edit3, Trash2, Loader2 } from "lucide-react";
import { Pill } from "@/components/layout/PageShell";
import MasterCrudShell from "@/components/layout/MasterCrudShell";
import { DataToolbar, DataTable } from "@/components/layout/DataTable";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

const EMPTY_FORM = {
  name: "",
  is_active: true,
};

const PER_PAGE = 25;

function apiError(err, fallback) {
  const errors = err.response?.data?.errors;
  if (errors) return Object.values(errors).flat()[0];
  return err.response?.data?.message || fallback;
}

function useDebounced(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export default function MainMenuMaster() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [view, setView] = useState("form");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const branchId = user?.default_branch?.id;
  const debouncedSearch = useDebounced(search);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["main-menus", "paged", branchId, page, debouncedSearch],
    queryFn: async () => {
      const { data: res } = await api.get("/main-menus", {
        params: {
          branch_id: branchId,
          page,
          per_page: PER_PAGE,
          search: debouncedSearch || undefined,
        },
      });
      return res;
    },
    enabled: Boolean(branchId),
    placeholderData: keepPreviousData,
  });

  const rows = data?.main_menus ?? [];
  const meta = data?.meta ?? {};
  const total = meta.total ?? rows.length;

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name: form.name.trim(),
        is_active: form.is_active,
      };
      if (editingId) {
        return api.put(`/main-menus/${editingId}`, payload, {
          params: { branch_id: branchId },
        });
      }
      return api.post("/main-menus", payload, {
        params: { branch_id: branchId },
      });
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["main-menus"] });
      queryClient.invalidateQueries({ queryKey: ["menu-items"] });
      toast.success(res.data.message);
      setEditingId(null);
      setForm(EMPTY_FORM);
      setView("form");
    },
    onError: (err) => toast.error(apiError(err, "Failed to save category")),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) =>
      api.delete(`/main-menus/${id}`, { params: { branch_id: branchId } }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["main-menus"] });
      queryClient.invalidateQueries({ queryKey: ["sub-menus"] });
      queryClient.invalidateQueries({ queryKey: ["menu-items"] });
      toast.success(res.data.message || "Category deleted.");
    },
    onError: (err) => toast.error(apiError(err, "Failed to delete category")),
  });

  function openNew() {
    setEditingId(null);
    setForm(EMPTY_FORM);
  }

  function openEdit(row) {
    setEditingId(row.id);
    setForm({
      name: row.name || "",
      is_active: row.is_active ?? row.status === "Active",
    });
    setView("form");
  }

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleSave() {
    if (!form.name.trim()) {
      toast.error("Category name is required.");
      return;
    }
    saveMutation.mutate();
  }

  return (
    <MasterCrudShell
      title="Category Master"
      subtitle={`${total} categories — ${user?.default_branch?.name || "branch"}`}
      view={view}
      onViewChange={setView}
      isNew={!editingId}
      onSave={handleSave}
      saving={saveMutation.isPending}
      onAddNew={openNew}
      testIdPrefix="main-menu"
      formTitle={editingId ? form.name || "Edit category" : "New category"}
      formSubtitle={editingId ? "Edit" : "Create"}
      list={
        <>
          <DataToolbar
            searchValue={search}
            onSearch={setSearch}
            testIdPrefix="main-menu"
            extra={
              isFetching && !isLoading ? (
                <Loader2 size={16} className="animate-spin text-slate-400" />
              ) : null
            }
          />
          {isLoading ? (
            <div className="h-32 grid place-items-center text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : (
            <DataTable
              testIdPrefix="main-menu"
              emptyText="No categories yet"
              onRowClick={openEdit}
              pagination={{
                page: meta.current_page || page,
                lastPage: meta.last_page || 1,
                total,
                from: meta.from || 0,
                to: meta.to || 0,
                onPageChange: setPage,
              }}
              columns={[
                { key: "name", label: "Category", bold: true },
                { key: "subMenus", label: "Subcategories", align: "right" },
                { key: "items", label: "Items", align: "right" },
                {
                  key: "status",
                  label: "Status",
                  render: (v) => (
                    <Pill tone={v === "Active" ? "green" : "default"}>{v}</Pill>
                  ),
                },
              ]}
              rows={rows}
              rowActions={(row) => [
                {
                  title: "Edit",
                  icon: <Edit3 size={14} />,
                  onClick: () => openEdit(row),
                },
                {
                  title: "Delete",
                  icon: <Trash2 size={14} />,
                  danger: true,
                  onClick: () => {
                    if (Number(row.subMenus) > 0) {
                      toast.error("Remove or reassign subcategories before deleting.");
                      return;
                    }
                    if (window.confirm(`Delete category "${row.name}"?`)) {
                      deleteMutation.mutate(row.id);
                    }
                  },
                },
              ]}
            />
          )}
        </>
      }
    >
      <div className="space-y-4 max-w-lg">
        <div>
          <Label className="text-[12px] text-slate-600 mb-1 block">Name *</Label>
          <Input
            value={form.name}
            onChange={(e) => setField("name", e.target.value)}
            className="h-9"
            placeholder="e.g. Hot Kitchen"
            data-testid="main-menu-name"
            autoFocus
          />
        </div>
        <div className="flex items-center gap-2 h-9 px-3 border rounded-md bg-white max-w-xs">
          <Switch
            checked={form.is_active}
            onCheckedChange={(v) => setField("is_active", v)}
          />
          <span className="text-[12px] text-slate-700">Active</span>
        </div>
      </div>
    </MasterCrudShell>
  );
}
